import { useState, useRef, useEffect, useCallback } from "react";
import { Box, Container, Paper, Button, Fade } from "@mui/material";
import { ArrowBack, ArrowForward, CheckCircle } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useOnboarding } from "../../context/OnboardingContext";

import SimplifiedWelcomeStep from "./steps/SimplifiedWelcomeStep";
import DocumentUploadStep from "./steps/DocumentUploadStep";
import SimpleSchedulingStep from "./steps/SimpleSchedulingStep";
import WidgetCodeStep from "./steps/WidgetCodeStep";

import ProgressStepper from "../../components/ProgressStepper";
import knowledgebaseAgentService from "../../services/knowledgebase-agent.service";
import { showMessage } from "../../customhooks/useNotifier";

const steps = [
  { id: "knowledge-base", title: "Welcome", component: SimplifiedWelcomeStep },
  { id: "upload-documents", title: "Documents", component: DocumentUploadStep },
  { id: "scheduling", title: "Scheduling", component: SimpleSchedulingStep },
  { id: "widget-code", title: "Widget Code", component: WidgetCodeStep },
];

const MultiStepForm = ({ mode = "add", agentId, initialData = {} }) => {
  const navigate = useNavigate();
  const {
    currentStep,
    completeStepAndAdvance,
    goToPreviousStep,
    getAllSteps,
    onboardingData,
    resetOnboarding,
  } = useOnboarding();

  const stepRef = useRef(null);
  const [ready, setReady] = useState(false);

  const [formData, setFormData] = useState({
    id: initialData?.id || null,
    knowledgeName: initialData?.knowledgeName || "",
    description: initialData?.description || "",
    websiteUrl: initialData?.websiteUrl || "",
    title: initialData?.title || "",
    contactEmail: initialData?.contactEmail || "",
    phoneNumber: initialData?.phoneNumber || "",
    crawlStatus: initialData?.crawlStatus || "queued",
    defaultPrompts: initialData?.defaultPrompts || [],
    scheduleEnabled: initialData?.scheduleEnabled || false,
    scheduleConnected: initialData?.scheduleConnected || false,

    // Sitemap (ADD MODE ONLY)
    sitemapLinks: [],
    selectedSitemapLinks: [],
  });

  const updateFormData = useCallback((values) => {
    setFormData((prev) => ({ ...prev, ...values }));
  }, []);

  const currentStepObject = steps.find((s) => s.id === currentStep) || steps[0];
  const StepComponent = currentStepObject.component;

  /* Reset onboarding on fresh add */
  useEffect(() => {
    if (mode === "add" && !localStorage.getItem("onboardingState")) {
      resetOnboarding();
    }
    const t = setTimeout(() => setReady(true), 50);
    return () => clearTimeout(t);
  }, [mode, resetOnboarding]);

  /* Restore step state */
  useEffect(() => {
    if (!ready) return;

    const saved = onboardingData?.[currentStep];
    if (saved) {
      setFormData((prev) => ({ ...prev, ...saved }));
    }
  }, [ready, currentStep, onboardingData]);

  /* ---------------- NEXT HANDLER ---------------- */
  const handleNext = async () => {
    if (stepRef.current?.validateStep) {
      const valid = stepRef.current.validateStep();
      if (!valid) {
        showMessage("Please fix all required fields.", "error");
        return;
      }
    }

    let updatedData = { ...formData };

    /* ---------- CREATE / UPDATE KNOWLEDGE BASE ---------- */
    if (currentStep === "knowledge-base") {
      try {
        const payload = {
          knowledgeName: formData.knowledgeName,
          description: formData.description || "",
          websiteUrl: formData.websiteUrl || "",
          title: formData.title || "",
          contactEmail: formData.contactEmail || "",
          phoneNumber: formData.phoneNumber || "",
          crawlStatus: "queued",
          defaultPrompts: [],
        };

        let kbId = formData.id;

        if (!kbId) {
          const result = await knowledgebaseAgentService.createKnowledgeBase(
            payload
          );

          kbId = result.id;
          updatedData = { ...updatedData, id: kbId };
          setFormData(updatedData);
        } else {
          await knowledgebaseAgentService.updateKnowledgeBase(kbId, payload);
        }

        /* ---------- STORE SITEMAP LINKS (AFTER KB CREATED) ---------- */
        if (mode === "add" && formData.selectedSitemapLinks?.length > 0) {
          await knowledgebaseAgentService.storeSitemapLinks(
            kbId,
            formData.selectedSitemapLinks
          );
        }
      } catch (err) {
        console.error("KB step failed:", err);
        showMessage(err?.message || "Failed to save knowledge base", "error");
        return;
      }
    }

    completeStepAndAdvance(currentStep, updatedData);

    /* ---------- FINAL STEP ---------- */
    if (currentStep === "widget-code") {
      showMessage("Setup complete!", "success");
      resetOnboarding();
      setTimeout(() => navigate("/app/agent"), 600);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f8fafc" }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <ProgressStepper variant="horizontal" />

        <Fade in timeout={500}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 6 },
              borderRadius: 3,
              backgroundColor: "white",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              border: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            <StepComponent
              ref={stepRef}
              formData={formData}
              updateFormData={updateFormData}
              mode={mode}
            />
          </Paper>
        </Fade>

        <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={goToPreviousStep}
            disabled={
              getAllSteps().findIndex((s) => s.id === currentStep) === 0
            }
          >
            Previous
          </Button>

          <Button
            variant="contained"
            endIcon={
              getAllSteps().findIndex((s) => s.id === currentStep) <
              steps.length - 1 ? (
                <ArrowForward />
              ) : (
                <CheckCircle />
              )
            }
            onClick={handleNext}
          >
            {getAllSteps().findIndex((s) => s.id === currentStep) <
            steps.length - 1
              ? "Next"
              : "Complete Setup"}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default MultiStepForm;
